import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, BookOpen, X } from 'lucide-react';
import OptimizedImage from '../../components/common/OptimizedImage';
import { Post } from '../../constants/posts';

interface BlogCardProps {
  post: Post;
  isExpanded: boolean;
  onExpand: () => void;
  onCollapse: () => void;
  cardRef: React.RefObject<HTMLDivElement> | null;
}

const BlogCard: React.FC<BlogCardProps> = ({ post, isExpanded, onExpand, onCollapse, cardRef }) => {
  const excerptRef = useRef<HTMLDivElement>(null);
  const wasExpanded = useRef(isExpanded);

  useEffect(() => {
    if (wasExpanded.current && !isExpanded && excerptRef.current) {
      excerptRef.current.scrollTop = 0;
    }
    wasExpanded.current = isExpanded;
  }, [isExpanded]);

  return (
    <div
      ref={cardRef}
      className={`group flex bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 border border-stone-100 
        ${isExpanded 
          ? 'flex-col lg:flex-row lg:col-span-2 lg:row-span-1' 
          : 'flex-col hover:-translate-y-1'
        }`}
    >
      {/* Image Section */}
      <Link 
        to={`/blog/${post.slug}`}
        className={`relative overflow-hidden bg-stone-100 shrink-0
          ${isExpanded 
            ? 'h-56 lg:h-auto lg:w-2/5' 
            : 'h-56'
          }`}
      >
        {post.image ? (
          <div className="w-full h-full transform group-hover:scale-105 transition-transform duration-500">
            <OptimizedImage
              src={post.image}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-contessa-50 text-contessa-300">
            <BookOpen className="w-12 h-12" />
          </div>
        )}
        
        <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
      </Link>

      {/* Content Section */}
      <div className={`flex flex-col flex-grow p-6 justify-start items-start ${isExpanded ? 'lg:p-8' : ''}`}>
        <div className="flex items-center justify-between mb-3 w-full">
          <div className="flex items-center text-xs font-medium text-contessa-600 space-x-2">
            <Calendar className="w-4 h-4" />
            <time>{post.date}</time>
          </div>
          
          {/* Close button when expanded */}
          {isExpanded && (
            <button
              onClick={(e) => {
                e.preventDefault();
                onCollapse();
              }}
              className="p-1.5 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-500 hover:text-stone-700 transition-colors"
              aria-label="Cerrar"
            >
              <X size={16} />
            </button>
          )}
        </div>

        <Link to={`/blog/${post.slug}`}>
          <h2 className={`font-bold text-stone-800 mb-3 group-hover:text-contessa-700 transition-colors leading-tight
            ${isExpanded ? 'text-2xl lg:text-3xl' : 'text-xl line-clamp-2'}`}>
            {post.title}
          </h2>
        </Link>

        {/* Excerpt - Clickable to expand */}
        <div
          ref={excerptRef}
          onClick={(e) => {
            if (!isExpanded) {
              e.preventDefault();
              onExpand();
            }
          }}
          onKeyDown={(e) => {
            if (!isExpanded && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              onExpand();
            }
          }}
          role={!isExpanded ? 'button' : undefined}
          tabIndex={!isExpanded ? 0 : undefined}
          aria-label={!isExpanded ? 'Expandir extracto del artículo' : undefined}
          className={`text-stone-600 text-sm leading-relaxed mb-4 w-full
            ${isExpanded
              ? 'lg:text-base lg:overflow-y-auto lg:max-h-64 lg:custom-scrollbar lg:pr-2'
              : 'overflow-hidden cursor-pointer hover:text-stone-800 transition-colors'
            }`}
          style={!isExpanded ? {
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          } : undefined}
        >
          <p className="m-0">{post.excerpt}</p>
        </div>

        <Link 
          to={`/blog/${post.slug}`}
          className="mt-auto pt-4 flex items-center text-contessa-600 font-semibold text-sm hover:translate-x-1 transition-transform duration-300"
        >
          Leer artículo completo <ArrowRight className="ml-2 w-4 h-4" />
        </Link>
      </div>
    </div>
  );
};

export default BlogCard;
